import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Mail, Phone, Linkedin, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { getCategories } from '../services/api';

// WhatsApp Icon Component
const WhatsAppIcon = ({ size = 18, className = "" }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);

  // Fetch categories for the dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProductsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Products', href: '/products', hasDropdown: true },
    { name: 'Services', href: '/services' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setProductsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setProductsDropdownOpen(false);
    }, 150);
  };

  const handleCategoryClick = (categoryId) => {
    setProductsDropdownOpen(false);
    setMobileMenuOpen(false);
    // Navigate to products page with category pre-selected via state
    navigate('/products', { state: { selectedCategoryId: categoryId } });
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top Contact Bar */}
      <div className="bg-[var(--primary-teal-dark)] py-2">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-end space-x-4 sm:space-x-6">
            <a 
              href="mailto:info@tritonglobaltrade.com" 
              className="flex items-center space-x-2 text-white hover:text-[var(--gold-light)] transition-colors text-sm"
              data-testid="top-email-link"
            >
              <Mail size={16} />
              <span className="hidden sm:inline">info@tritonglobaltrade.com</span>
            </a>
            <a 
              href="tel:+919819618390" 
              className="flex items-center space-x-2 text-white hover:text-[var(--gold-light)] transition-colors text-sm"
              data-testid="top-phone-link"
            >
              <Phone size={16} />
              <span className="hidden sm:inline">+91 9819618390</span>
            </a>
            <a 
              href="https://wa.me/919819618390" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-white hover:text-[#25D366] transition-colors text-sm"
              data-testid="top-whatsapp-link"
            >
              <WhatsAppIcon size={16} />
              <span className="hidden sm:inline">WhatsApp</span>
            </a>
            <a 
              href="https://in.linkedin.com/in/hasmitapatel5" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-white hover:text-[var(--gold-light)] transition-colors text-sm"
              data-testid="top-linkedin-link"
            >
              <Linkedin size={16} />
              <span className="hidden sm:inline">LinkedIn</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-white/95 backdrop-blur-md shadow-sm gold-border-bottom">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28">
          {/* Logo Section */}
          <Link to="/" className="flex items-center space-x-4">
            <img 
              src="/triton-icon-logo.png" 
              alt="Triton Global Trade" 
              className="h-20 w-20 object-contain"
            />
            <div className="text-2xl sm:text-3xl font-bold tracking-wide" style={{ color: 'var(--primary-teal)' }}>
              TRITON GLOBAL TRADE
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigation.map((item) => (
              item.hasDropdown ? (
                <div 
                  key={item.name}
                  className="relative"
                  ref={dropdownRef}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    to={item.href}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-1 ${
                      isActive(item.href)
                        ? 'text-white'
                        : 'hover:bg-[var(--cream-bg)]'
                    }`}
                    style={{
                      backgroundColor: isActive(item.href) ? 'var(--primary-teal)' : 'transparent',
                      color: isActive(item.href) ? 'white' : 'var(--text-dark)'
                    }}
                    data-testid="products-nav-link"
                  >
                    {item.name}
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform ${productsDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </Link>
                  
                  {/* Dropdown Menu */}
                  {productsDropdownOpen && (
                    <div 
                      className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50 max-h-96 overflow-y-auto"
                      data-testid="products-dropdown"
                    >
                      <Link
                        to="/products"
                        onClick={() => setProductsDropdownOpen(false)}
                        className="block px-4 py-2 text-sm font-semibold text-[var(--primary-teal)] hover:bg-[var(--cream-bg)] border-b border-gray-100"
                      >
                        View All Categories
                      </Link>
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => handleCategoryClick(category.id)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-[var(--cream-bg)] hover:text-[var(--primary-teal)] transition-colors"
                          data-testid={`dropdown-category-${category.id}`}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    isActive(item.href)
                      ? 'text-white'
                      : 'hover:bg-[var(--cream-bg)]'
                  }`}
                  style={{
                    backgroundColor: isActive(item.href) ? 'var(--primary-teal)' : 'transparent',
                    color: isActive(item.href) ? 'white' : 'var(--text-dark)'
                  }}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/contact">
              <Button className="btn-gold font-semibold">
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-md"
            style={{ color: 'var(--primary-teal)' }}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              {navigation.map((item) => (
                item.hasDropdown ? (
                  <div key={item.name}>
                    <button
                      onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-md text-base font-medium transition-colors ${
                        isActive(item.href) ? 'text-white' : ''
                      }`}
                      style={{
                        backgroundColor: isActive(item.href) ? 'var(--primary-teal)' : 'transparent',
                        color: isActive(item.href) ? 'white' : 'var(--text-dark)'
                      }}
                    >
                      {item.name}
                      <ChevronDown 
                        size={20} 
                        className={`transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    
                    {/* Mobile Categories Dropdown */}
                    {mobileProductsOpen && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-[var(--gold-accent)] pl-4">
                        <Link
                          to="/products"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setMobileProductsOpen(false);
                          }}
                          className="block py-2 text-sm font-semibold text-[var(--primary-teal)]"
                        >
                          View All Categories
                        </Link>
                        {categories.slice(0, 10).map((category) => (
                          <button
                            key={category.id}
                            onClick={() => {
                              handleCategoryClick(category.id);
                              setMobileProductsOpen(false);
                            }}
                            className="block w-full text-left py-2 text-sm text-gray-600 hover:text-[var(--primary-teal)]"
                          >
                            {category.name}
                          </button>
                        ))}
                        {categories.length > 10 && (
                          <Link
                            to="/products"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setMobileProductsOpen(false);
                            }}
                            className="block py-2 text-sm text-[var(--gold-accent)] font-medium"
                          >
                            + {categories.length - 10} more categories
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-3 rounded-md text-base font-medium transition-colors ${
                      isActive(item.href) ? 'text-white' : ''
                    }`}
                    style={{
                      backgroundColor: isActive(item.href) ? 'var(--primary-teal)' : 'transparent',
                      color: isActive(item.href) ? 'white' : 'var(--text-dark)'
                    }}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="btn-gold w-full mt-2">
                  Get Quote
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
      </div>

      {/* Tagline Bar */}
      <div className="bg-[var(--primary-teal)] py-4 text-center">
        <p className="text-white text-lg sm:text-xl italic font-semibold tracking-wider">
          Bridging Markets, Building Trust
        </p>
      </div>
    </header>
  );
};

export default Header;
